"use client";

import React from "react";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function TerminAnfragen() {
  return <section id="termin-anfragen" className="py-24 bg-background relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/30 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-serif text-foreground mb-4">
            Termin Anfrage
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Nimm dir Zeit für deine innere Entwicklung. Fülle das untenstehende Formular aus, um einen Termin für eine Sitzung anzufragen. Ich melde mich zeitnah bei dir.
          </p>
          <p className="text-muted-foreground text-sm mt-4 italic">
            Die Kosten werden nicht von der Krankenkasse übernommen. Ich ersetze keinen Arzt.
          </p>
        </div>

        <div className="max-w-3xl mx-auto bg-card rounded-xl shadow-sm border border-border/50 p-6 md:p-10">
          <FormProvider formId="appointment_request_form_support" sectionName="termin-anfragen" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          message: "",
          data_privacy_consent: false
        }} validationRules={{
          full_name: {
            required: true
          },
          email: {
            required: true
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          data_privacy_consent: {
            required: true
          }
        }} submitText="Termin anfragen" successMessage="Vielen Dank für deine Anfrage. Ich habe deine Daten erhalten und werde mich in Kürze bei dir melden, um den Termin zu bestätigen.">
            <AppointmentForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function AppointmentForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in duration-500">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
          <CircleCheck className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif text-foreground mb-3">Anfrage erfolgreich gesendet</h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="h-auto py-3 px-6">
          Weitere Anfrage senden
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <FormField control={control} name="full_name" rules={{
        required: "Bitte gib deinen Namen ein."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Vollständiger Name</FormLabel>
              <FormControl>
                <Input placeholder="Anna Müller" className="h-auto py-4 px-4 bg-background/50 border-border focus-visible:border-primary focus-visible:ring-primary/20 transition-colors" {...field} />
              </FormControl>
              <FormDescription>Bitte gib deinen Vor- und Nachnamen ein.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Bitte gib deine E-Mail-Adresse ein.",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Bitte gib eine gültige E-Mail-Adresse ein."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">E-Mail-Adresse</FormLabel>
              <FormControl>
                <Input type="email" placeholder="anna@example.ch" className="h-auto py-4 px-4 bg-background/50 border-border focus-visible:border-primary focus-visible:ring-primary/20 transition-colors" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Bitte gib deine Telefonnummer ein."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Telefonnummer</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+41 79 123 45 67" className="h-auto py-4 px-4 bg-background/50 border-border focus-visible:border-primary focus-visible:ring-primary/20 transition-colors" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Nachricht / Grund des Termins</FormLabel>
            <FormControl>
              <Textarea placeholder="Kurze Beschreibung deines Anliegens" className="min-h-[140px] bg-background/50 border-border focus-visible:border-primary focus-visible:ring-primary/20 transition-colors py-4 px-4" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="data_privacy_consent" rules={{
      required: "Bitte stimme der Datenschutzerklärung zu."
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border/50 p-4 bg-background/30">
            <FormControl>
              <input type="checkbox" className="mt-1 size-4 rounded border-border text-primary focus:ring-primary/20 transition-colors cursor-pointer" checked={field.value} onChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-normal text-muted-foreground cursor-pointer">
                Ich stimme der Verarbeitung meiner Daten gemäss{" "}
                <Link to="/ImprintPage" className="text-primary hover:underline font-medium">
                  Datenschutzerklärung
                </Link>{" "}
                zu.
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <CircleAlert className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-auto py-4 text-base font-medium bg-primary/90 hover:bg-primary transition-colors duration-300">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Wird gesendet...
            </> : 'Termin anfragen'}
        </Button>
      </div>
    </Form>;
}